import { motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { Copy, ScanLine, MessageCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { UPI_ID, UPI_PAYEE, UPI_URL, waLink } from "../constants/site";
import { ChapterHeader } from "./ChapterHeader";

const STEPS = [
  { icon: ScanLine, text: "Scan the QR with any UPI app — GPay, PhonePe, Paytm" },
  { icon: ShieldCheck, text: `Pay securely to ${UPI_PAYEE}` },
  { icon: MessageCircle, text: "WhatsApp us the screenshot — booking confirmed instantly" },
];

export const PaymentSection = () => {
  const copyUpi = async () => {
    try {
      await navigator.clipboard.writeText(UPI_ID);
      toast.success("UPI ID copied to clipboard");
    } catch {
      toast.error("Could not copy — please type it manually");
    }
  };

  return (
    <section id="pay" data-testid="payment-section" className="relative bg-obsidian px-6 py-28 md:px-10 md:py-36">
      <div className="mx-auto max-w-7xl">
        <ChapterHeader
          index="06"
          title="Pay Securely"
          testid="payment-header"
          heading={
            <>
              Scan. Pay. <span className="italic text-coral">Fly.</span>
            </>
          }
          blurb="Lock your package or visa slot in seconds. No gateways, no card fees — just a direct UPI transfer to Gautam Travel."
        />

        <div className="grid items-center gap-14 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8 }}
          >
            <div className="space-y-8">
              {STEPS.map((s, i) => (
                <div key={i} className="flex items-start gap-5" data-testid={`payment-step-${i + 1}`}>
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-sunset/40 bg-sunset/10 text-sunset">
                    <s.icon className="h-5 w-5" />
                  </span>
                  <p className="pt-2.5 text-base font-light text-white/70 md:text-lg">{s.text}</p>
                </div>
              ))}
            </div>

            <button
              onClick={copyUpi}
              data-testid="upi-copy-btn"
              className="group mt-12 inline-flex items-center gap-3 rounded-full border border-white/15 px-6 py-3.5 text-sm text-white/80 transition-colors duration-300 hover:border-sunset hover:text-sunset"
            >
              <span className="font-mono tracking-wide" data-testid="upi-id-text">{UPI_ID}</span>
              <Copy className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="flex justify-center lg:justify-end"
          >
            <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-white/[0.04] p-10 text-center backdrop-blur-xl">
              <p className="text-[10px] uppercase tracking-[0.35em] text-white/50">Scan with any UPI app</p>
              <div className="mx-auto mt-6 w-fit rounded-2xl bg-white p-5 shadow-[0_0_50px_rgba(255,107,53,0.2)]">
                <QRCodeSVG value={UPI_URL} size={210} level="M" data-testid="payment-qr-code" />
              </div>
              <p className="mt-6 font-display text-2xl">{UPI_PAYEE}</p>
              <p className="mt-1 font-mono text-sm text-white/50">{UPI_ID}</p>
              <a
                href={waLink("Hi Gautam Travel! I've just made a UPI payment. Sharing the screenshot here.")}
                target="_blank"
                rel="noreferrer"
                data-testid="payment-whatsapp-confirm-btn"
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-teal py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-teal/80"
              >
                <MessageCircle className="h-4 w-4" />
                Confirm on WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
